import "../../component/evaluation/MuiButtonWrapper.scss";

import BarChartIcon from "@mui/icons-material/BarChart";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import SsidChartIcon from "@mui/icons-material/SsidChart";
import {
  Box,
  Button,
  Card,
  Collapse,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Stack } from "@mui/system";
import { DateTime } from "luxon";
import raw from "raw.macro";
import { useState } from "react";
import React from "react";
import ReactMarkdown from "react-markdown";
import { useParams } from "react-router-dom";
import rehypeRaw from "rehype-raw";
import useSWR from "swr";

import Breadcrumbs from "../../component/breadcrumbs";
import CustomHelmet from "../../component/CustomHelmet";
import {
  BarChart,
  LineChart,
  QuestionDialog,
  RadarChart,
  SubdimensionDialog,
} from "../../component/evaluation";
import style from "../../component/evaluation/RadarChart.module.scss";
import FullScreenLoader from "../../component/FullScreenLoader";
import Intro from "../../component/Intro";
import BaseContainer from "../../component/layout/base-container";
import {
  diagramTypes,
  evaluationTypes,
  perspectiveLabels,
  perspectives,
} from "../../lib/diagramTypes";
import { auth_fetcher, fetcher } from "../../lib/fetcher";
import { components } from "../../lib/markdownComponentMapping";
import { perspectiveDetailKey } from "../../lib/perspectiveDetailKey";
import { SpiderChartIcon } from "../../lib/spiderChartIcon";

export default function Evaluation({ user }) {
  const all_perspectives = [
    perspectives.teacher,
    perspectives.expert,
    perspectives.student,
  ];

  const perspecitves_mapper = {
    [perspectives.teacher]: "L",
    [perspectives.expert]: "E",
    [perspectives.student]: "S",
  };

  const [displayedPerspectives, setDisplayedPerspectives] =
    useState(all_perspectives);

  const [diagramType, setDiagramType] = useState(diagramTypes.line);

  const [evaluationType, setEvaluationType] = useState(
    evaluationTypes.question
  );

  const [detailQuestion, setDetailQuestion] = useState(null);
  const [detailSubdimension, setDetailSubdimension] = useState(null);

  const [showInfo, setShowInfo] = useState(false);
  const toggleShowInfo = () => setShowInfo((prevState) => !prevState);

  const theme = useTheme();
  const params = useParams();
  const surveyID = params.surveyId;
  const { data: survey_data, error: survey_error } = useSWR(
    `${process.env.REACT_APP_BASE_URL}surveyruns/${surveyID}/?evaluation`,
    auth_fetcher
  );
  const { data: questions, error: qustions_error } = useSWR(
    `${process.env.REACT_APP_BASE_URL}question/`,
    fetcher
  );
  const { data: subdimensions, error: subdimensions_error } = useSWR(
    `${process.env.REACT_APP_BASE_URL}subdimension/`,
    fetcher
  );

  if (survey_error || qustions_error || subdimensions_error)
    return <div>failed to load</div>;

  if (!survey_data || !questions || !subdimensions) return <FullScreenLoader />;

  const averages_data = survey_data.evaluation[evaluationType].map(
    (evaluation) => {
      const graphic_data = {
        id: evaluation.questionId,
        name:
          evaluationType === evaluationTypes.question
            ? questions.results.find((q) => q.id === evaluation.questionId)[
                "title"
              ]
            : subdimensions.results.find(
                (s) => evaluation.subdmension_name === s.name
              )["title"],
        [perspectives.teacher]: evaluation["L"]["score__avg"],
        [perspectives.expert]: evaluation["E"]["score__avg"],
        [perspectives.student]: evaluation["S"]["score__avg"],
      };

      all_perspectives.forEach((perspective) => {
        const letter = perspecitves_mapper[perspective];
        const mean = evaluation[letter]["score__avg"];
        const stdev = evaluation[letter]["score__stddev"];
        graphic_data[`${perspective}-stdev`] = stdev;
        graphic_data[`${perspective}-stdev-values`] = [
          mean - stdev,
          mean + stdev,
        ];
        const individual_scores = evaluation[letter]["individual_scores"];
        individual_scores.forEach((score, i) => {
          graphic_data[perspectiveDetailKey(perspective, i)] = score;
        });
      });
      return graphic_data;
    }
  );

  const ablauf_text = raw("../../textblocke/evaluation.md");

  return (
    <>
      <CustomHelmet
        pageTitle={`Auswertung Umfrage in ${survey_data.subject}`}
      />
      <QuestionDialog
        question={detailQuestion}
        questions={questions.results}
        onClose={() => setDetailQuestion(null)}
      />
      <SubdimensionDialog
        subdimension={detailSubdimension}
        subdimensions={subdimensions.results}
        onClose={() => setDetailSubdimension(null)}
      />
      <Breadcrumbs
        listItems={[
          { name: "Home", url: "/" },
          { name: "Umfragen", url: "/dashboard" },
          {
            name: "Auswertung",
            url: `/dashboard/auswertung/${survey_data.id}`,
          },
        ]}
      />
      <BaseContainer>
        <Intro title="Auswertung">
          <h2 className="Intro--title mb-2 text-xl">
            {`Auswertung Ihrer Umfrage im Fach ${
              survey_data.subject
            } vom ${DateTime.fromISO(survey_data.date).toLocaleString(
              DateTime.DATE_MED
            )}`}
          </h2>
          <p className="mb-2">
            Hier finden Sie die Auswertungen zu Ihrer ausgewählten Umfrage.
            Anhand der Schaltflächen erkennen Sie, dass Sie die Möglichkeit
            haben, die Daten auf verschiedene Arten darzustellen. Auf diese
            Weise können Sie die Rückmeldungen aus verschiedenen Perspektiven
            beleuchten.
          </p>
          <p>
            Jede Darstellungsform hat ihre eigenen Vor- und Nachteile.
            Erläuterungen zu den Darstellungsformen und den Schaltflächen finden
            Sie unter "Hinweise".
          </p>
        </Intro>
      </BaseContainer>

      <BaseContainer oversize className="mui-btn-wrapper mb-8">
        <Stack
          direction="row"
          justifyContent="end"
          sx={{ paddingBottom: 1 }}
          spacing={2}
        >
          <Button
            onClick={toggleShowInfo}
            startIcon={showInfo ? <CloseIcon /> : <InfoIcon />}
          >
            Hinweise
          </Button>
        </Stack>
        <Collapse in={showInfo}>
          <ReactMarkdown components={components} rehypePlugins={[rehypeRaw]}>
            {ablauf_text}
          </ReactMarkdown>
          <Stack
            direction="row"
            justifyContent="center"
            sx={{ paddingTop: 1, paddingBottom: 1 }}
            spacing={2}
          >
            <Button onClick={() => setShowInfo(false)}>Hilfe schliessen</Button>
          </Stack>
        </Collapse>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item>
            <ToggleButtonGroup
              value={displayedPerspectives}
              onChange={(e, v) => {
                if (v !== null) setDisplayedPerspectives(v);
              }}
            >
              {all_perspectives.map((p, i) => (
                <ToggleButton key={i} value={p}>
                  {perspectiveLabels.get(p)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>
          <Grid item>
            <ToggleButtonGroup
              value={evaluationType}
              exclusive
              onChange={(e, v) => {
                if (v !== null) setEvaluationType(v);
              }}
            >
              <ToggleButton value="question">Einzelaussage</ToggleButton>
              <ToggleButton value="subdimension">Subdimension</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item>
            <ToggleButtonGroup
              value={diagramType}
              exclusive
              onChange={(e, v) => {
                if (v !== null) setDiagramType(v);
              }}
            >
              <ToggleButton value={diagramTypes.line}>
                <Tooltip
                  title={<span className="text-base">Liniendiagramm</span>}
                >
                  <span>
                    <SsidChartIcon />
                  </span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value={diagramTypes.bar}>
                <Tooltip
                  title={<span className="text-base">Balkendiagramm</span>}
                >
                  <span>
                    <BarChartIcon />
                  </span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value={diagramTypes.spider}>
                <Tooltip
                  title={<span className="text-base">Spiderdiagramm</span>}
                >
                  <span>
                    <SpiderChartIcon />
                  </span>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
        <Card>
          {displayedPerspectives.length < 1 ? (
            <Box sx={{ height: theme.spacing(4), margin: theme.spacing(4) }}>
              <Typography sx={{ textAlign: "center" }}>
                Bitte wählen Sie mindestens eine Perspektive an um die
                Auswertung anzuzeigen.
              </Typography>
            </Box>
          ) : diagramType === diagramTypes.line ? (
            <Grid container>
              <Grid
                item
                xs={
                  displayedPerspectives.includes(perspectives.student) ? 6 : 12
                }
                sx={{
                  transition: theme.transitions.create([
                    "flex-basis",
                    "max-width",
                  ]),
                  ...(!(
                    displayedPerspectives.includes(perspectives.teacher) ||
                    displayedPerspectives.includes(perspectives.expert)
                  ) && {
                    maxWidth: "0px !important",
                    display: "none",
                  }),
                }}
              >
                <h2 className="mb-2 mt-6 pl-4 text-lg font-semibold">
                  Einschätzung des Angebots
                </h2>
                <LineChart
                  evaluationType={evaluationType}
                  data={averages_data}
                  displayedPerspectives={displayedPerspectives.filter(
                    (perspective) =>
                      [perspectives.teacher, perspectives.expert].includes(
                        perspective
                      )
                  )}
                  all_perspectives={all_perspectives.filter((perspective) =>
                    [perspectives.teacher, perspectives.expert].includes(
                      perspective
                    )
                  )}
                  displayQuestionDetails={(question_title) => {
                    const questionName = questions.results.find(
                      (q) => q.title === question_title
                    )["name"];
                    setDetailQuestion(questionName);
                  }}
                  displaySubdimensionDetails={(subdimension_title) => {
                    const subdimensionName = subdimensions.results.find(
                      (s) => s.title === subdimension_title
                    )["name"];
                    setDetailSubdimension(subdimensionName);
                  }}
                />
              </Grid>
              <Grid
                item
                xs={
                  displayedPerspectives.includes(perspectives.teacher) ||
                  displayedPerspectives.includes(perspectives.expert)
                    ? 6
                    : 12
                }
                sx={{
                  transition: theme.transitions.create([
                    "flex-basis",
                    "max-width",
                  ]),
                  ...(!displayedPerspectives.includes(perspectives.student) && {
                    maxWidth: "0px !important",
                    display: "none",
                  }),
                }}
              >
                <h2 className="mb-2 mt-6 pl-4 text-lg font-semibold">
                  Einschätzung der Nutzung
                </h2>
                <LineChart
                  evaluationType={evaluationType}
                  data={averages_data}
                  displayedPerspectives={displayedPerspectives.filter(
                    (perspective) => perspective === perspectives.student
                  )}
                  all_perspectives={all_perspectives.filter(
                    (perspective) => perspective === perspectives.student
                  )}
                  displayQuestionDetails={(question_title) => {
                    const questionName = questions.results.find(
                      (q) => q.title === question_title
                    )["name"];
                    setDetailQuestion(questionName);
                  }}
                  displaySubdimensionDetails={(subdimension_title) => {
                    const subdimensionName = subdimensions.results.find(
                      (s) => s.title === subdimension_title
                    )["name"];
                    setDetailSubdimension(subdimensionName);
                  }}
                />
              </Grid>
            </Grid>
          ) : diagramType === diagramTypes.bar ? (
            <BarChart
              survey_data={survey_data}
              evaluationType={evaluationType}
              questions={questions}
              subdimension={subdimensions}
              all_perspectives={all_perspectives}
              displayedPerspectives={displayedPerspectives}
              displayQuestionDetails={(question) => setDetailQuestion(question)}
              displaySubdimensionDetails={(subdimension) =>
                setDetailSubdimension(subdimension)
              }
            />
          ) : diagramType === diagramTypes.spider ? (
            <Grid
              container
              className={
                (displayedPerspectives.includes(perspectives.student) &&
                  !displayedPerspectives.includes(perspectives.expert) &&
                  !displayedPerspectives.includes(perspectives.teacher)) ||
                (!displayedPerspectives.includes(perspectives.student) &&
                  displayedPerspectives.includes(perspectives.expert) &&
                  displayedPerspectives.includes(perspectives.teacher))
                  ? style.oneChart
                  : style.twoCharts
              }
            >
              <Grid
                item
                xs={
                  displayedPerspectives.includes(perspectives.student) ? 6 : 12
                }
                sx={{
                  transition: theme.transitions.create([
                    "flex-basis",
                    "max-width",
                  ]),
                  ...(!(
                    displayedPerspectives.includes(perspectives.teacher) ||
                    displayedPerspectives.includes(perspectives.expert)
                  ) && {
                    maxWidth: "0px !important",
                    display: "none",
                  }),
                }}
              >
                <h2 className="mb-2 mt-6 pl-4 text-lg font-semibold">
                  Einschätzung des Angebots
                </h2>
                <RadarChart
                  evaluationType={evaluationType}
                  averagesData={averages_data}
                  displayedPerspectives={displayedPerspectives.filter(
                    (perspective) =>
                      [perspectives.teacher, perspectives.expert].includes(
                        perspective
                      )
                  )}
                  all_perspectives={all_perspectives.filter((perspective) =>
                    [perspectives.teacher, perspectives.expert].includes(
                      perspective
                    )
                  )}
                  displayQuestionDetails={(question_title) => {
                    const questionName = questions.results.find(
                      (q) => q.title === question_title
                    )["name"];
                    setDetailQuestion(questionName);
                  }}
                  displaySubdimensionDetails={(subdimension_title) => {
                    const subdimensionName = subdimensions.results.find(
                      (s) => s.title === subdimension_title
                    )["name"];
                    setDetailSubdimension(subdimensionName);
                  }}
                />
              </Grid>
              <Grid
                item
                xs={
                  displayedPerspectives.includes(perspectives.teacher) ||
                  displayedPerspectives.includes(perspectives.expert)
                    ? 6
                    : 12
                }
                sx={{
                  transition: theme.transitions.create([
                    "flex-basis",
                    "max-width",
                  ]),
                  ...(!displayedPerspectives.includes(perspectives.student) && {
                    maxWidth: "0px !important",
                    display: "none",
                  }),
                }}
              >
                <h2 className="mb-2 mt-6 pl-4 text-lg font-semibold">
                  Einschätzung der Nutzung
                </h2>
                <RadarChart
                  evaluationType={evaluationType}
                  averagesData={averages_data}
                  displayedPerspectives={displayedPerspectives.filter(
                    (perspective) => perspective === perspectives.student
                  )}
                  all_perspectives={all_perspectives.filter(
                    (perspective) => perspective === perspectives.student
                  )}
                  displayQuestionDetails={(question_title) => {
                    const questionName = questions.results.find(
                      (q) => q.title === question_title
                    )["name"];
                    setDetailQuestion(questionName);
                  }}
                  displaySubdimensionDetails={(subdimension_title) => {
                    const subdimensionName = subdimensions.results.find(
                      (s) => s.title === subdimension_title
                    )["name"];
                    setDetailSubdimension(subdimensionName);
                  }}
                />
              </Grid>
            </Grid>
          ) : (
            "okay, this is like properly broken"
          )}
        </Card>

        <div className="mt-8 pl-4">
          <strong className="font-semibold">Skala:</strong>
          <table className="mb-4">
            <tbody>
              <tr>
                <td className="pr-2">1</td>
                <td>Stimmt nicht</td>
              </tr>
              <tr>
                <td>2</td>
                <td>Stimmt eher nicht</td>
              </tr>
              <tr>
                <td>3</td>
                <td>Stimmt eher</td>
              </tr>
              <tr>
                <td>4</td>
                <td>Stimmt genau</td>
              </tr>
            </tbody>
          </table>
          <p>
            <span className="font-semibold">Aufgaben: </span>
            Die Aufgaben sind gehaltvoll und auf das kognitive Niveau der
            Schüler*innen abgestimmt. Einzelaussagen: Offenheit,
            Anspruchsniveau, Transfer
          </p>
          <p>
            <span className="font-semibold">Unterstützung: </span>
            Die kognitive Aktivität der Schüler*innen wird unterstützt.
            Einzelaussagen: Begründung, Anforderungsgehalt, Diskursivität
          </p>
          <p>
            <span className="font-semibold">Metakognition: </span>
            Aufgaben mit kognitiv aktivierendem Potenzial regen metakognitives
            Lernen der Schüler*innen an. Einzelaussagen: Auseinandersetzung,
            Reflexion, Selbsteinschätzung
          </p>
        </div>
      </BaseContainer>
    </>
  );
}
